import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Save, Package, TrendingUp, Clock, AlertTriangle, Gauge, Copy, ExternalLink } from "lucide-react";
import { calculateUPM, calculateUptimeUPM, getYieldColor, formatYield } from "@/utils/productionCalculations";
import CompletedWorkOrderSummary from "./CompletedWorkOrderSummary";
import "./WorkOrderDetailModal.css";

const WorkOrderDetailModal = ({ open, onOpenChange, workOrder, items, onUpdate, onDelete, onClone }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    production_line: "",
    customer_po: "", 
    item_id: "",     
    item_name: "",   
    planned_date: "",
    planned_start_time: "",
    planned_end_time: "",
    planned_quantity: "",
    actual_date: "",
    actual_start_time: "",
    actual_end_time: "",
    actual_quantity: "",
    status: "",
    downtime_minutes: "",
    notes: "",
    bom_usage: []
  });

  useEffect(() => {
    if (workOrder) {
      setFormData({
        production_line: workOrder.production_line || "",
        customer_po: workOrder.customer_po || "", 
        item_id: workOrder.item_id || "",         
        item_name: workOrder.item_name || "",    
        planned_date: workOrder.planned_date || "",
        planned_start_time: workOrder.planned_start_time || "",
        planned_end_time: workOrder.planned_end_time || "",
        planned_quantity: workOrder.planned_quantity?.toString() || "",
        actual_date: workOrder.actual_date || "",
        actual_start_time: workOrder.actual_start_time || "",
        actual_end_time: workOrder.actual_end_time || "",
        actual_quantity: workOrder.actual_quantity?.toString() || "",
        status: workOrder.status || "scheduled",
        downtime_minutes: workOrder.downtime_minutes?.toString() || "0",
        notes: workOrder.notes || "",
        bom_usage: workOrder.bom_usage || []
      });
    }
  }, [workOrder]);

  const isEditable = workOrder?.status === "scheduled";

  const handleSave = () => {
    if (!isEditable) return;
    
    const updateData = {
      production_line: formData.production_line,
      customer_po: formData.customer_po, 
      item_id: formData.item_id,         
      item_name: formData.item_name,     
      planned_date: formData.planned_date,
      planned_start_time: formData.planned_start_time,
      planned_end_time: formData.planned_end_time,
      planned_quantity: parseFloat(formData.planned_quantity) || 0,
      actual_date: formData.actual_date || null,
      actual_start_time: formData.actual_start_time || null,
      actual_end_time: formData.actual_end_time || null,
      actual_quantity: parseFloat(formData.actual_quantity) || 0,
      status: formData.status,
      downtime_minutes: parseFloat(formData.downtime_minutes) || 0,
      notes: formData.notes,
      bom_usage: formData.bom_usage
    };
    onUpdate(updateData);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this work order?")) {
      onDelete(workOrder.wo_id);
    }
  };

  const handleBOMUpdate = (index, field, value) => {
    const updatedBOM = [...formData.bom_usage];
    updatedBOM[index] = { ...updatedBOM[index], [field]: parseFloat(value) || 0 };
    
    // Calculate yield percentage and waste
    if (field === "actual_qty" || field === "planned_qty") {
      const planned = updatedBOM[index].planned_qty;
      const actual = updatedBOM[index].actual_qty;
      if (planned > 0) {
        updatedBOM[index].yield_pct = ((actual / planned) * 100).toFixed(2);
        updatedBOM[index].waste_qty = Math.max(0, planned - actual);
      }
    }
    
    setFormData({ ...formData, bom_usage: updatedBOM });
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="detail-modal-content" data-testid="work-order-detail-modal" aria-describedby="detail-wo-description">
        <DialogHeader>
          <p id="detail-wo-description" className="sr-only">
            View and edit work order details including BOM components and production tracking
          </p>
          <div className="detail-modal-header">
            <div>
              <DialogTitle className="detail-modal-title">
                {workOrder.wo_number}
              </DialogTitle>
              <p className="detail-modal-subtitle" data-testid="detail-item-name">{workOrder.item_name}</p>
            </div>
            <Badge 
              className={`detail-status-badge status-${formData.status}`}
              data-testid="detail-wo-status"
            >
              {formData.status.replace("_", " ")}
            </Badge>
          </div>
        </DialogHeader>

        {/* Show summary view for completed work orders, tabs for others */}
        {formData.status === "completed" ? (
          <div className="completed-summary-container" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '1rem' }}>
            <CompletedWorkOrderSummary workOrder={workOrder} items={items} />
          </div>
        ) : (
          <Tabs defaultValue="general" className="detail-tabs">
            <TabsList className="tabs-list">
              <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
              <TabsTrigger value="bom" data-testid="bom-tab">
                BOM & Yields
                {formData.bom_usage.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{formData.bom_usage.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="production" data-testid="production-tab">Production</TabsTrigger>
            </TabsList>

          <TabsContent value="general" className="tab-content">
            {!isEditable && (
              <div className="read-only-notice">
                <span>⚠️ This work order is {formData.status.replace('_', ' ')} and cannot be edited from the Schedule page.</span>
              </div>
            )}
            <div className="form-grid">
              <div className="col-span-2">
                <Label htmlFor="item_edit">Item (Changing this will reset BOM)</Label>
                <Select
                  value={formData.item_id}
                  onValueChange={(value) => {
                    const selectedItem = items.find(i => i.id === value || i.item_id === value);
                    setFormData({
                      ...formData, 
                      item_id: value,
                      item_name: selectedItem?.displayname || selectedItem?.itemid || selectedItem?.name || ""
                    });
                  }}
                  disabled={!isEditable}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.item_name || "Select Item"} />
                  </SelectTrigger>
                  <SelectContent>
                    {items
                      .filter(item => item.is_assembly_item === true || item.itemtype === "Assembly")
                      .map(item => (
                        <SelectItem key={item.id || item.item_id} value={item.id || item.item_id}>
                          {item.displayname || item.name || item.itemid}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="production_line">Production Line</Label>
                <Select 
                  value={formData.production_line} 
                  onValueChange={(value) => setFormData({...formData, production_line: value})}
                  disabled={!isEditable}
                >
                  <SelectTrigger data-testid="detail-production-line-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1</SelectItem>
                    <SelectItem value="P2">P2</SelectItem>
                    <SelectItem value="P3">P3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="customer_po_edit">Customer PO #</Label>
                <Input
                  id="customer_po_edit"
                  value={formData.customer_po}
                  onChange={(e) => setFormData({...formData, customer_po: e.target.value})}
                  disabled={!isEditable}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({...formData, status: value})}
                  disabled={!isEditable}
                >
                  <SelectTrigger data-testid="detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="planned_date">Planned Date</Label>
                <Input
                  id="planned_date"
                  type="date"
                  value={formData.planned_date}
                  onChange={(e) => setFormData({...formData, planned_date: e.target.value})}
                  disabled={!isEditable}
                  data-testid="detail-planned-date-input"
                />
              </div>
              <div>
                <Label htmlFor="planned_start_time">Start Time</Label>
                <Input
                  id="planned_start_time"
                  type="time"
                  value={formData.planned_start_time}
                  onChange={(e) => setFormData({...formData, planned_start_time: e.target.value})}
                  disabled={!isEditable}
                  data-testid="detail-planned-start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="planned_end_time">End Time</Label>
                <Input
                  id="planned_end_time"
                  type="time"
                  value={formData.planned_end_time}
                  onChange={(e) => setFormData({...formData, planned_end_time: e.target.value})}
                  disabled={!isEditable}
                  data-testid="detail-planned-end-time-input"
                />
              </div>
              <div>
                <Label htmlFor="planned_quantity">Planned Quantity</Label>
                <Input
                  id="planned_quantity"
                  type="number"
                  value={formData.planned_quantity}
                  onChange={(e) => setFormData({...formData, planned_quantity: e.target.value})}
                  disabled={!isEditable}
                  data-testid="detail-planned-quantity-input"
                />
              </div>
              <div>
                <Label htmlFor="downtime">Downtime (minutes)</Label>
                <Input
                  id="downtime"
                  type="number"
                  value={formData.downtime_minutes}
                  onChange={(e) => setFormData({...formData, downtime_minutes: e.target.value})}
                  disabled={!isEditable}
                  data-testid="detail-downtime-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  disabled={!isEditable}
                  data-testid="detail-notes-textarea"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bom" className="tab-content">
            <div className="bom-section">
              <h3 className="bom-title">Bill of Materials & Component Tracking (View Only)</h3>
              {formData.bom_usage.length === 0 ? (
                <div className="empty-bom">
                  <Package className="w-12 h-12 text-gray-400 mb-2" />
                  <p>No BOM components defined for this item</p>
                </div>
              ) : (
                <div className="bom-list">
                  {formData.bom_usage.map((component, index) => {
                    const yieldColor = getYieldColor(component.yield_pct, component.item_type);
                    const isFood = component.item_type === 'food';
                    const displayUnit = component.unit;
                    // All quantities are now in proper units (lbs for food, units for packaging)
                    const plannedDisplay = component.planned_qty;
                    const actualDisplay = component.actual_qty;
                    const wasteDisplay = component.waste_qty || 0;
                    
                    return (
                      <Card key={index} className="bom-card">
                        <CardHeader className="bom-card-header">
                          <CardTitle className="bom-component-name">
                            {component.component_name}
                            <Badge variant="outline" className="ml-2 text-xs">
                              {component.item_type}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="bom-card-content">
                          <div className="bom-grid">
                            <div>
                              <Label className="bom-label">Planned Qty</Label>
                              <div className="bom-value">
                                {plannedDisplay} {displayUnit}
                              </div>
                            </div>
                            <div>
                              <Label className="bom-label">Actual Qty</Label>
                              <div className="bom-value">
                                {actualDisplay} {displayUnit}
                              </div>
                            </div>
                            <div>
                              <Label className="bom-label">Yield %</Label>
                              <div 
                                className="yield-display" 
                                data-testid={`bom-yield-${index}`}
                                style={{ 
                                  color: yieldColor === 'green' ? '#16a34a' : yieldColor === 'orange' ? '#ea580c' : '#dc2626',
                                  fontWeight: yieldColor === 'red' ? 'bold' : 'normal'
                                }}
                              >
                                <TrendingUp className={`w-4 h-4`} style={{ color: yieldColor === 'green' ? '#16a34a' : yieldColor === 'orange' ? '#ea580c' : '#dc2626' }} />
                                {formatYield(component.yield_pct)}%
                              </div>
                            </div>
                            <div>
                              <Label className="bom-label">Waste</Label>
                              <div className="waste-display" data-testid={`bom-waste-${index}`}>
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                {wasteDisplay} {displayUnit}
                              </div>
                            </div>
                          </div>
                          
                          {/* Lot Allocations */}
                          {component.lots && component.lots.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <Label className="bom-label text-xs font-semibold mb-2 block">Lot Traceability</Label>
                              <div className="space-y-1">
                                {component.lots.map((lot, lotIndex) => (
                                  <div key={lotIndex} className="flex justify-between items-center text-xs bg-gray-50 p-2 rounded">
                                    <span className="font-mono text-gray-700">{lot.lot_number}</span>
                                    <span className="text-gray-600">
                                      {lot.actual_qty} {component.unit}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="production" className="tab-content">
            <h3 className="bom-title">Production Data (View Only)</h3>
            <div className="form-grid">
              <div>
                <Label htmlFor="actual_date">Actual Date</Label>
                <div className="production-value">
                  {formData.actual_date || "Not recorded"}
                </div>
              </div>
              <div>
                <Label htmlFor="actual_start_time">Actual Start Time</Label>
                <div className="production-value">
                  {formData.actual_start_time || "Not recorded"}
                </div>
              </div>
              <div>
                <Label htmlFor="actual_end_time">Actual End Time</Label>
                <div className="production-value">
                  {formData.actual_end_time || "Not recorded"}
                </div>
              </div>
              <div className="col-span-2">
                <Label htmlFor="actual_quantity">Actual Quantity Produced</Label>
                <div className="production-value">
                  {formData.actual_quantity || 0} cases
                </div>
              </div>
              
              {/* UPM Calculations */}
              {formData.status === "completed" && formData.actual_start_time && formData.actual_end_time && (
                <>
                  <Separator className="col-span-2 my-4" />
                  <Card className="col-span-2" style={{ background: '#f0f9ff', borderColor: '#3b82f6' }}>
                    <CardHeader>
                      <CardTitle className="summary-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Gauge className="w-5 h-5 text-blue-600" />
                        Performance Metrics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="summary-grid">
                        <div className="summary-item">
                          <div>
                            <p className="summary-label">UPM (Units Per Minute)</p>
                            <p className="summary-value text-2xl font-bold text-blue-600" data-testid="upm-value">
                              {(() => {
                                // Find the matching item, which might fail
                                const item = items?.find(i => i.item_id === workOrder.item_id);

                                // Safely get the units per case with a default value
                                const unitsPerCase = item?.units_per_case || 12;

                                const upm = calculateUPM(
                                  formData.actual_quantity,
                                  unitsPerCase,
                                  formData.actual_start_time,
                                  formData.actual_end_time
                                );
                                return upm ? `${upm}` : 'N/A';
                              })()}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Total units / Total time</p>
                          </div>
                        </div>
                        <div className="summary-item">
                          <div>
                            <p className="summary-label">Uptime UPM</p>
                            <p className="summary-value text-2xl font-bold text-green-600" data-testid="uptime-upm-value">
                              {(() => {
                                // Find the matching item, which might fail
                                const item = items?.find(i => i.item_id === workOrder.item_id);

                                // Safely get the units per case with a default value
                                const unitsPerCase = item?.units_per_case || 12;

                                const uptimeUpm = calculateUptimeUPM(
                                  formData.actual_quantity,
                                  unitsPerCase,
                                  formData.actual_start_time,
                                  formData.actual_end_time,
                                  parseFloat(formData.downtime_minutes || 0)
                                );
                                return uptimeUpm ? `${uptimeUpm}` : 'N/A';
                              })()}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Excluding downtime</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
              
              <Separator className="col-span-2 my-4" />
              
              <Card className="col-span-2 production-summary">
                <CardHeader>
                  <CardTitle className="summary-title">Production Summary</CardTitle>
                </CardHeader>
                <CardContent className="summary-content">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <Package className="summary-icon text-blue-600" />
                      <div>
                        <p className="summary-label">Planned vs Actual</p>
                        <p className="summary-value" data-testid="summary-planned-vs-actual">
                          {formData.planned_quantity} / {formData.actual_quantity || 0}
                        </p>
                      </div>
                    </div>
                    <div className="summary-item">
                      <TrendingUp className="summary-icon text-green-600" />
                      <div>
                        <p className="summary-label">Efficiency</p>
                        <p className="summary-value" data-testid="summary-efficiency">
                          {formData.planned_quantity > 0
                            ? ((parseFloat(formData.actual_quantity || 0) / parseFloat(formData.planned_quantity)) * 100).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                    </div>
                    <div className="summary-item">
                      <Clock className="summary-icon text-amber-600" />
                      <div>
                        <p className="summary-label">Total Downtime</p>
                        <p className="summary-value" data-testid="summary-downtime">
                          {formData.downtime_minutes || 0} min
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        )}

        <div className="modal-footer">
          <div className="footer-left-actions">
            <Button 
              variant="outline"
              onClick={() => onClone(workOrder)}
              className="clone-btn"
              data-testid="clone-work-order-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Clone Work Order
            </Button>
            {isEditable && (
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                className="delete-btn"
                data-testid="delete-work-order-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
          <div className="footer-actions">
            <Button 
              variant="outline" 
              onClick={() => {
                onOpenChange(false);
                navigate(`/work-order/${workOrder.wo_id}`);
              }} 
              data-testid="view-full-page-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Page
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-detail-btn">
              Close
            </Button>
            {isEditable && (
              <Button onClick={handleSave} data-testid="save-work-order-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderDetailModal;
